import leadGenerationImage from "@/assets/images/BB Data.webp";
import {
  ArrowRight,
  CheckCircle2,
  CreditCard,
  Download,
  Eye,
  FileSpreadsheet,
  Mail,
  Search,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";

export default function DataDownloadSection() {

  const workflow = [
    {
      icon: Search,
      title: "Tell Us What Kind of Leads You Need",
      desc: "Enter a simple prompt, such as Leading Home Decor Importers in the United States.",
    },
    {
      icon: Eye,
      title: "Preview Matching Lead Data",
      desc: "See the number of available contacts and data coverage before purchasing.",
    },
    {
      icon: CreditCard,
      title: "Choose How Many Contacts You Need",
      desc: "Select the number of contacts you want and pay only for your selection.",
    },
    {
      icon: Download,
      title: "Download Your Data as an Excel Sheet",
      desc: "Download names, verified emails, job titles, company details, and profile links.",
    },
  ];

  const highlights = [
    {
      icon: Users,
      title: "Decision-maker contacts",
      desc: "Reach people who match your target role and location.",
    },
    {
      icon: ShieldCheck,
      title: "Verified business fields",
      desc: "Company, contact, email, title, location, and LinkedIn data.",
    },
    {
      icon: FileSpreadsheet,
      title: "Excel-ready export",
      desc: "A clean file your sales team can use immediately.",
    },
  ];

  return (
    <section className="relative overflow-hidden bg-white py-16 max-md:py-10">
      <div className="absolute inset-x-0 top-0 h-44 bg-gradient-to-b from-blue-50 to-transparent pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none opacity-[0.04]">
        <div className="h-full w-full bg-[linear-gradient(90deg,currentColor_1px,transparent_1px),linear-gradient(180deg,currentColor_1px,transparent_1px)] bg-[size:44px_44px] text-blue-600" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <div>
              <div className="inline-flex max-md:flex max-md:w-fit max-md:mx-auto items-center gap-2 rounded-full bg-blue-100 px-4 py-2 mb-6 text-sm font-semibold text-blue-700">
                <Sparkles className="h-4 w-4" />
                New Feature
              </div>

              <h2 className="max-w-2xl max-md:text-center ">
                Download Verified
                <span className="ml-2 text-blue-600">Business Data</span>
              </h2>
            </div>

            <p className="text-base max-md:text-center max-md:text-base text-gray-600 leading-relaxed mt-5 max-w-2xl">
              Search with a simple prompt and download targeted company contacts
              with verified names, email addresses, company details, and
              outreach-ready fields.
            </p>

            <div className="mt-8 rounded-2xl border border-gray-100 bg-white shadow-xl overflow-hidden">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-5 border-b border-gray-100">
                <div>
                  <p className="max-md:hidden max-md:pl-4 text-xs uppercase tracking-wide text-gray-500 mb-2">
                    Search Prompt
                  </p>
                  <div className="max-md:flex hidden items-end mb-4">
                    <div className="h-11 w-11 max-md:h-7 max-md:w-7 rounded-xl bg-blue-600 text-white flex items-center justify-center flex-shrink-0">
                      <Search className="h-5 w-5 " />
                    </div>
                    <p className="max-md:pl-4 text-xs uppercase tracking-wide text-gray-500 mb-2">
                      Search Prompt
                    </p>
                  </div>
                  <p className="font-semibold text-gray-900">
                    "Leading Home Décor Importers in the United States"
                  </p>
                </div>
                <div className="h-11 w-11 max-md:hidden rounded-xl bg-blue-600 text-white flex items-center justify-center flex-shrink-0">
                  <Search className="h-5 w-5" />
                </div>
              </div>

              <div className="divide-y divide-gray-100">
                {workflow.map((item, index) => (
                  <div key={item.title} className="flex gap-4 p-5">
                    <div className="relative flex flex-col items-center">
                      <div className="h-10 w-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                        <item.icon className="h-5 w-5" />
                      </div>
                      {index < workflow.length - 1 && (
                        <div className="absolute top-12 h-full w-px bg-blue-100" />
                      )}
                    </div>

                    <div className="pb-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-blue-600">
                          0{index + 1}
                        </span>
                        <h4 className=" text-xl text-gray-900 mb-0">
                          {item.title}
                        </h4>
                      </div>
                      <p className="text-sm text-gray-500 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-blue-100 via-indigo-50 to-white blur-2xl opacity-80" />

            <div className="relative overflow-hidden">
              <div className="relative">
                <img
                  src={leadGenerationImage}
                  alt="AI lead generation dashboard"
                  className="w-full aspect-[1.15/1] object-cover"
                />
              </div>

              <div className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-green-50 text-green-600">
                    <FileSpreadsheet className="h-6 w-6" />
                  </div>

                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="mb-0 text-sm font-semibold text-gray-900 sm:text-base">
                        leads.xlsx
                      </h4>

                      <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-1 text-[11px] font-semibold text-green-700">
                        <CheckCircle2 className="h-3 w-3" />
                        Ready
                      </span>
                    </div>

                    <p className="mt-1 text-xs text-gray-500 sm:text-sm">
                      Names, verified emails and company details
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3 sm:justify-end max-md:justify-center">
                  <span className="text-xs font-medium text-gray-500">
                    Excel file
                  </span>

                  <button
                    type="button"
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700"
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {highlights.map((item) => (
            <div
              key={item.title}
              className="flex gap-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-md"
            >
              <div className="h-11 w-11 rounded-xl bg-gray-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                <item.icon className="h-5 w-5" />
              </div>
              <div>
                <h4 className="font-semibold text-xl text-gray-900 mb-1">
                  {item.title}
                </h4>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
